import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Modal } from 'react-bootstrap';
import { IWatrRequest } from '@nkr/models/index';
import { canOfferDevMockDocs, resolveDevMockEditKey } from './gate';
import {
    DEV_MOCK_BATCH_OPTIONS,
    DevMockBatchType,
    DevMockReceiptInput,
    DevMockSlot
} from './constants';
import {
    buildReceiptMockSlots,
    buildWageMockSlots,
    dismissForRequest,
    isDismissedForRequest,
    isWageSlotKnownMissing,
    probeMissingSlots
} from './inventory';
import { probeDownloadOk } from './probe';
import { applyBatchVariant, buildDevMockSnapshot } from './snapshot';
import { generateDevMockFile } from './generator';

interface IProps {
    data: IWatrRequest;
    downloadFile: (docType: number, lookupId?: number, includeReceipts?: boolean) => Promise<any>;
    uploadDevMock: (args: {
        requestId: number;
        editKey: string;
        docType: number;
        lookupId: number;
        file: File;
    }) => Promise<boolean>;
    receiptFileIds?: number[];
    receiptInputs?: DevMockReceiptInput[];
    includeWages?: boolean;
    includeReceipts?: boolean;
    forceWageSlots?: { forceSsn?: boolean; forceTax?: boolean; forcePaystub?: boolean };
    onComplete?: () => void;
}

const DevMockDocsOffer: React.FC<IProps> = (props) => {
    const [open, setOpen] = useState(false);
    const [missing, setMissing] = useState<DevMockSlot[]>([]);
    const [selected, setSelected] = useState<{ [key: string]: boolean }>({});
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState('');
    const [batchType, setBatchType] = useState<DevMockBatchType>('mixed');
    const probeGenRef = useRef(0);
    const editKey = resolveDevMockEditKey(props.data as any);
    const requestId = props.data && props.data.id;
    const receiptIdsKey = (props.receiptFileIds || []).join(',');
    const forceSsn = !!(props.forceWageSlots && props.forceWageSlots.forceSsn);
    const forceTax = !!(props.forceWageSlots && props.forceWageSlots.forceTax);
    const forcePaystub = !!(props.forceWageSlots && props.forceWageSlots.forcePaystub);

    useEffect(() => {
        if (!canOfferDevMockDocs(editKey) || !requestId) {
            return;
        }
        if (isDismissedForRequest(requestId)) {
            return;
        }
        if (typeof props.downloadFile !== 'function') {
            return;
        }

        const wageSlots = props.includeWages === false
            ? []
            : buildWageMockSlots(props.data, {
                forceSsn,
                forceTax,
                forcePaystub
            });
        const receiptSlots = props.includeReceipts === false
            ? []
            : buildReceiptMockSlots(props.receiptFileIds || [], props.receiptInputs);
        const slots = wageSlots.concat(receiptSlots);
        if (!slots.length) {
            return;
        }

        const probeGen = ++probeGenRef.current;

        (async () => {
            const foundMissing = await probeMissingSlots(
                slots,
                (docType, lookupId) => probeDownloadOk(props.downloadFile, docType, lookupId),
                undefined,
                (slot) => isWageSlotKnownMissing(props.data, slot)
            );
            if (probeGen !== probeGenRef.current || !foundMissing.length) {
                return;
            }
            const sel: { [key: string]: boolean } = {};
            foundMissing.forEach(s => { sel[s.key] = true; });
            setMissing(foundMissing);
            setSelected(sel);
            setOpen(true);
        })();
    }, [
        requestId,
        editKey,
        receiptIdsKey,
        props.includeWages,
        props.includeReceipts,
        forceSsn,
        forceTax,
        forcePaystub,
        props.data,
        props.downloadFile,
        props.receiptFileIds,
        props.receiptInputs
    ]);

    const toggle = (key: string) => {
        setSelected(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const close = () => {
        setOpen(false);
    };

    const dismiss = () => {
        dismissForRequest(props.data.id);
        setOpen(false);
    };

    const generate = async () => {
        const editKey = resolveDevMockEditKey(props.data as any);
        if (!canOfferDevMockDocs(editKey)) {
            setStatus('Not available');
            return;
        }
        const chosen = missing.filter(s => selected[s.key]);
        if (!chosen.length) {
            return;
        }
        setBusy(true);
        setStatus('Preparing mock files…');
        let okCount = 0;
        for (let i = 0; i < chosen.length; i++) {
            const slot = chosen[i];
            if (!canOfferDevMockDocs(editKey)) {
                break;
            }
            setStatus(`Generating ${slot.label} (${i + 1} of ${chosen.length})…`);
            try {
                const receiptMeta = (props.receiptInputs || []).find(r => r.fileId === slot.lookupId);
                const base = buildDevMockSnapshot(props.data, receiptMeta);
                const snapshot = applyBatchVariant(base, slot.kind, batchType, i);
                const file = await generateDevMockFile(slot.kind, snapshot, props.data.id, batchType);
                setStatus(`Uploading ${slot.label} (${i + 1} of ${chosen.length})…`);
                const ok = await props.uploadDevMock({
                    requestId: props.data.id,
                    editKey,
                    docType: slot.docType,
                    lookupId: slot.lookupId,
                    file
                });
                if (ok) {
                    okCount += 1;
                }
            } catch {
            }
        }
        setBusy(false);
        setStatus(okCount === chosen.length
            ? `Ready — ${okCount} mock document(s) uploaded. Re-open View to load them.`
            : `Uploaded ${okCount} of ${chosen.length}. Failed items can be retried.`);
        if (okCount > 0) {
            setOpen(false);
            if (props.onComplete) {
                props.onComplete();
            }
        }
    };

    const wageMissing = missing.filter(s => s.category === 'Wage');
    const expenseMissing = missing.filter(s => s.category === 'Expense');
    const selectedCount = missing.filter(s => selected[s.key]).length;

    const renderSlot = (slot: DevMockSlot) => (
        <Checkbox
            key={slot.key}
            checked={!!selected[slot.key]}
            disabled={busy}
            onChange={() => toggle(slot.key)}
            style={{ marginTop: 4, marginBottom: 4 }}
        >
            <span style={{ fontWeight: 600 }}>{slot.label}</span>
            {slot.detail ? (
                <span style={{ display: 'block', fontSize: 12, color: '#4b5563', fontWeight: 400 }}>
                    {slot.detail}
                </span>
            ) : null}
            {slot.secondary ? (
                <span style={{ display: 'block', fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>
                    {slot.secondary}
                </span>
            ) : null}
        </Checkbox>
    );

    const visible = open && missing.length > 0;

    return (
        <Modal
            show={visible}
            onHide={busy ? undefined : close}
            backdrop="static"
            className="modal-vertical-center"
            style={visible ? { display: 'flex', alignItems: 'center' } : undefined}
        >
            <Modal.Header closeButton={!busy}>
                <Modal.Title>Generate test documents</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p style={{ marginBottom: 8 }}>
                    <strong>{missing.length}</strong> document{missing.length === 1 ? '' : 's'} could not be loaded
                    for request #{props.data.id}.
                    Generate mock files and upload them so Admin verify / approve can continue locally.
                </p>
                <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>
                    Dev / test EditKey only. Not real donor files. Choose a batch style below before generating.
                </p>

                <div style={{ marginBottom: 16 }}>
                    <div style={{
                        fontSize: 11,
                        fontWeight: 650,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: '#9ca3af',
                        marginBottom: 8
                    }}>
                        Batch type
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {DEV_MOCK_BATCH_OPTIONS.map(opt => (
                            <label
                                key={opt.id}
                                style={{
                                    display: 'block',
                                    border: batchType === opt.id ? '1px solid #0f766e' : '1px solid #e5e7eb',
                                    background: batchType === opt.id ? '#f0fdfa' : '#fff',
                                    borderRadius: 6,
                                    padding: '10px 12px',
                                    cursor: busy ? 'default' : 'pointer',
                                    margin: 0
                                }}
                            >
                                <input
                                    type="radio"
                                    name="devMockBatchType"
                                    value={opt.id}
                                    checked={batchType === opt.id}
                                    disabled={busy}
                                    onChange={() => setBatchType(opt.id)}
                                    style={{ marginRight: 8 }}
                                />
                                <span style={{ fontWeight: 600 }}>{opt.label}</span>
                                <span style={{ display: 'block', marginLeft: 22, fontSize: 12, color: '#6b7280', fontWeight: 400 }}>
                                    {opt.description}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>

                {wageMissing.length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                        <div style={{
                            fontSize: 11,
                            fontWeight: 650,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: '#9ca3af',
                            marginBottom: 6
                        }}>
                            Wage · {wageMissing.length}
                        </div>
                        <div style={{
                            maxHeight: 220,
                            overflow: 'auto',
                            border: '1px solid #e5e7eb',
                            borderRadius: 6,
                            padding: '8px 12px'
                        }}>
                            {wageMissing.map(renderSlot)}
                        </div>
                    </div>
                )}

                {expenseMissing.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                        <div style={{
                            fontSize: 11,
                            fontWeight: 650,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: '#9ca3af',
                            marginBottom: 6
                        }}>
                            Expenses · {expenseMissing.length}
                        </div>
                        <div style={{
                            maxHeight: 280,
                            overflow: 'auto',
                            border: '1px solid #e5e7eb',
                            borderRadius: 6,
                            padding: '8px 12px'
                        }}>
                            {expenseMissing.map(renderSlot)}
                        </div>
                    </div>
                )}

                {status ? <p style={{ marginTop: 12, color: '#6b7280', marginBottom: 0 }}>{status}</p> : null}
            </Modal.Body>
            <Modal.Footer>
                <span style={{ float: 'left', fontSize: 12, color: '#9ca3af', lineHeight: '30px' }}>
                    {selectedCount} selected
                </span>
                <Button disabled={busy} onClick={dismiss}>Don&apos;t ask again</Button>
                <Button disabled={busy} onClick={close}>Not now</Button>
                <Button bsStyle="primary" disabled={busy || selectedCount === 0} onClick={generate}>
                    {busy ? 'Working…' : `Generate selected (${selectedCount})`}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default DevMockDocsOffer;
